// apps/client/src/services/organisation.ts

import {
  Organisation,
  OrganisationCreateResponse,
  OrganisationProfileUpdateResponse,
  PartialOrganisationUpdate,
  UserSwitchOrganisationResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

/**
 * Create a new organisation
 */
export const createOrganisation = async (): Promise<OrganisationCreateResponse> => {
  const response = await apiClient.post(`/organisations/create`);

  if (!response.ok) {
    console.error('Failed to create organisation:', response);
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || 'Failed to create organisation. Please try again.';
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Switch to a different organisation
 */
export const switchOrganisation = async (
  organisationId: string
): Promise<UserSwitchOrganisationResponse> => {
  const response = await apiClient.put(`/user/organisations/${organisationId}/switch`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || 'Failed to switch organisation. Please try again.';
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Get organisation profile details
 */
export const getOrganisationProfile = async (organisationId: string): Promise<Organisation> => {
  try {
    const response = await apiClient.get(`/organisations/${organisationId}/profile`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // The API returns data wrapped in a 'response' property
    return data.organisation;
  } catch (error) {
    console.error('Error in getOrganisationProfile:', error);
    throw error;
  }
};

/**
 * Update organisation profile details
 */
export const updateOrganisationProfile = async (
  organisationId: string,
  updateData: PartialOrganisationUpdate
): Promise<OrganisationProfileUpdateResponse> => {
  const response = await apiClient.put(`/organisations/${organisationId}/profile`, updateData);

  if (!response.ok) {
    console.error('Failed to update organisation profile:', response);
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || 'Failed to update organisation details. Please try again.';
    throw new Error(errorMessage);
  }

  return response.json();
};
